import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  deleteObservabilityConfig,
  getObservabilityConfig,
  putObservabilityConfig,
  type ObservabilityConfig,
  type ObservabilityPutInput,
} from '@/services/observabilityApi';
import { isLocalMode } from '@/services/localRuntimeClient';

const buildKey = (projectName: string) => ['observabilityConfig', projectName] as const;

/**
 * Fetches the observability configuration for a project.
 * Returns `null` (not undefined) when the project has no configuration —
 * components branch on `data === null` to render the empty state.
 */
export const useObservabilityConfig = (projectName: string) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery<ObservabilityConfig | null>({
    queryKey: buildKey(projectName),
    queryFn: async () => {
      if (isLocalMode()) {
        return getObservabilityConfig(projectName, '');
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return getObservabilityConfig(projectName, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName,
    staleTime: 30 * 1000,
    refetchInterval: false,
  });
};

/**
 * Mutation hook for applying (create / update) the observability
 * configuration. On success the PUT response is written straight into the
 * query cache so the UI doesn't race with the runtime's eventual
 * propagation to per-app maintenance jobs.
 *
 * To remove the configuration, use `useDeleteObservabilityConfig`.
 */
export const useUpdateObservabilityConfig = (projectName: string) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ObservabilityConfig, Error, ObservabilityPutInput>({
    mutationFn: async (input) => {
      if (isLocalMode()) {
        return putObservabilityConfig(projectName, '', input);
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return putObservabilityConfig(projectName, token, input);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(buildKey(projectName), data);
    },
  });
};

/**
 * Mutation hook for removing the observability configuration via DELETE.
 * On success we flip the cached query data to `null` so the UI re-renders
 * the "not configured" state without waiting on a refetch.
 */
export const useDeleteObservabilityConfig = (projectName: string) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (isLocalMode()) {
        return deleteObservabilityConfig(projectName, '');
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return deleteObservabilityConfig(projectName, token);
    },
    onSuccess: () => {
      queryClient.setQueryData(buildKey(projectName), null);
    },
  });
};
